import { Injectable, inject } from '@angular/core';

import { I18nService } from '@recruitee/i18n';
import {
  Category,
  EmploymentType,
  OfferEducation,
  OfferExperience,
  Priority,
  SalaryPeriod,
} from '@recruitee/offers-types';

import type { SelectOption } from '../forms/select/select.component';
import { FixedSelectOptionsTranslations } from './fixed-select-options.translations';

@Injectable({ providedIn: 'root' })
export class FixedSelectOptionsHelper {
  private readonly translations = inject(FixedSelectOptionsTranslations);
  private readonly i18n = inject(I18nService);
  // EMPLOYMENT TYPE
  private readonly employmentTypeSelectOptions: SelectOption[] = [
    {
      label: this.translations.employmentTypes[EmploymentType.FullTime],
      value: EmploymentType.FullTime,
      nestedOptions: [
        {
          label: this.i18n.translate(
            'app.options.offer.employment_types_v2.nested_options.permanent',
          ),
          value: EmploymentType.FullTimePermanent,
          displayedSelectedLabel:
            this.translations.employmentTypes[EmploymentType.FullTimePermanent],
        },
        {
          label: this.i18n.translate(
            'app.options.offer.employment_types_v2.nested_options.fixed_term',
          ),
          value: EmploymentType.FullTimeFixedTerm,
          displayedSelectedLabel:
            this.translations.employmentTypes[EmploymentType.FullTimeFixedTerm],
        },
      ],
    },
    {
      label: this.translations.employmentTypes[EmploymentType.PartTime],
      value: EmploymentType.PartTime,
      nestedOptions: [
        {
          label: this.i18n.translate(
            'app.options.offer.employment_types_v2.nested_options.permanent',
          ),
          value: EmploymentType.PartTimePermanent,
          displayedSelectedLabel:
            this.translations.employmentTypes[EmploymentType.PartTimePermanent],
        },
        {
          label: this.i18n.translate(
            'app.options.offer.employment_types_v2.nested_options.fixed_term',
          ),
          value: EmploymentType.PartTimeFixedTerm,
          displayedSelectedLabel:
            this.translations.employmentTypes[EmploymentType.PartTimeFixedTerm],
        },
        {
          label: this.i18n.translate(
            'app.options.offer.employment_types_v2.nested_options.minijob',
          ),
          value: EmploymentType.PartTimeMinijob,
          displayedSelectedLabel: this.translations.employmentTypes[EmploymentType.PartTimeMinijob],
          badgeText: this.i18n.translate(
            'app.options.offer.employment_types_v2.nested_options.minijob_badge',
          ),
        },
      ],
    },
    {
      label: this.translations.employmentTypes[EmploymentType.Freelance],
      value: EmploymentType.Freelance,
    },
    {
      label: this.translations.employmentTypes[EmploymentType.Contract],
      value: EmploymentType.Contract,
    },
    {
      label: this.translations.employmentTypes[EmploymentType.Temporary],
      value: EmploymentType.Temporary,
    },
    {
      label: this.translations.employmentTypes[EmploymentType.Internship],
      value: EmploymentType.Internship,
    },
    {
      label: this.translations.employmentTypes[EmploymentType.Apprenticeship],
      value: EmploymentType.Apprenticeship,
    },
    {
      label: this.translations.employmentTypes[EmploymentType.Seasonal],
      value: EmploymentType.Seasonal,
    },
    {
      label: this.translations.employmentTypes[EmploymentType.Volunteer],
      value: EmploymentType.Volunteer,
    },
  ];

  // EDUCATION
  private readonly educationSelectOptions: SelectOption[] = [
    ...Object.values(OfferEducation).map(key => ({
      label: this.translations.educations[<OfferEducation>key],
      value: key,
    })),
  ];

  // EXPERIENCE
  private readonly experienceSelectOptions: SelectOption[] = [
    ...Object.values(OfferExperience).map(key => ({
      label: this.translations.experiences[<OfferExperience>key],
      value: key,
    })),
  ];

  // CATEGORY
  private readonly categorySelectOptions: SelectOption[] = [
    ...Object.values(Category).map(key => ({
      label: this.translations.categories[<Category>key],
      value: key,
    })),
  ];

  // SALARY PERIOD
  private readonly salaryPeriodSelectOptions: SelectOption[] = [
    ...Object.values(SalaryPeriod).map(key => ({
      label: this.translations.salaryPeriods[<SalaryPeriod>key],
      value: key,
    })),
  ];

  private readonly prioritySelectOptions: SelectOption[] = [
    {
      label: this.translations.priorities[Priority.Low],
      value: Priority.Low,
      icon: 'flag',
      iconColorToken: 'colorIconLowDisabled',
    },
    {
      label: this.translations.priorities[Priority.Normal],
      value: Priority.Normal,
      icon: 'flag',
      iconColorToken: 'colorIconPrimaryDefault',
    },
    {
      label: this.translations.priorities[Priority.High],
      value: Priority.High,
      icon: 'flag',
      iconColorToken: 'colorIconWarningDefault',
    },
    {
      label: this.translations.priorities[Priority.Urgent],
      value: Priority.Urgent,
      icon: 'flag',
      iconColorToken: 'colorIconCriticalStrongDefault',
    },
  ];

  public getEmploymentTypeSelectOptions(): SelectOption[] {
    return this.employmentTypeSelectOptions;
  }

  public getEducationSelectOptions(): SelectOption[] {
    return this.educationSelectOptions;
  }

  public getCategorySelectOptions(): SelectOption[] {
    return this.categorySelectOptions;
  }

  public getExperienceSelectOptions(): SelectOption[] {
    return this.experienceSelectOptions;
  }

  public getSalaryPeriodSelectOptions(): SelectOption[] {
    return this.salaryPeriodSelectOptions;
  }

  public getPrioritySelectOptions(): SelectOption[] {
    return this.prioritySelectOptions;
  }
}
